import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, User, FileText, Filter, Download, Calendar } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { toast } from 'sonner';

export default function ActivityLogs({ token }) {
  const [activityLogs, setActivityLogs] = useState([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [filterUserId, setFilterUserId] = useState('all');
  const [filterAction, setFilterAction] = useState('all');
  const [filterEntityType, setFilterEntityType] = useState('all');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [quickDateFilter, setQuickDateFilter] = useState('all');
  
  const [teamMembers, setTeamMembers] = useState([]);
  const [userMap, setUserMap] = useState({}); // Map user_id to user details
  const [teamMembersLoaded, setTeamMembersLoaded] = useState(false);

  useEffect(() => {
    fetchTeamMembers();
  }, [token]);

  useEffect(() => {
    // Wait for team members to load before fetching activity logs
    if (token && teamMembersLoaded) {
      fetchActivityLogs();
    }
  }, [token, page, filterUserId, filterAction, filterEntityType, filterStartDate, filterEndDate, teamMembersLoaded]);

  const fetchTeamMembers = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/admin/team/members`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // API returns array directly, NOT {team_members: []}
        const members = Array.isArray(data) ? data : (data.team_members || []);
        setTeamMembers(members);
        
        // Build user map with BOTH member_id and email as keys
        const map = {};
        
        members.forEach(member => {
          const userData = {
            name: member.name || member.email,
            email: member.email
          };
          
          // Add by member_id
          if (member.member_id) {
            map[member.member_id] = userData;
          }
          
          // Add by email for lookup
          if (member.email) {
            map[member.email] = userData;
          }
          
          // CRITICAL: If this is the admin user, also map "admin" key
          if (member.email === 'rabi.saha@me.com' || member.role === 'admin') {
            map['admin'] = userData;
          }
        });
        
        setUserMap(map);
        setTeamMembersLoaded(true);
      }
    } catch (error) {
      console.error('Failed to fetch team members:', error);
      setTeamMembersLoaded(true); // Still allow activity logs to load even if this fails
    }
  };

  // Helper function to get user display name
  const getUserName = (userId, userEmail) => {
    // Try user_id first
    if (userMap[userId]) {
      return userMap[userId].name;
    }
    // Fallback to email lookup
    if (userEmail && userMap[userEmail]) {
      return userMap[userEmail].name;
    }
    // Last resort: return email or userId
    return userEmail || userId;
  };

  // Helper function to get user email (separate from name)
  const getUserEmail = (userId, userEmail) => {
    // Try user_id first
    if (userMap[userId]) {
      return userMap[userId].email;
    }
    // Fallback to email lookup
    if (userEmail && userMap[userEmail]) {
      return userMap[userEmail].email;
    }
    // Return the passed email
    return userEmail || '';
  };

  // Helper function to get relative time
  const getRelativeTime = (timestamp) => {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return formatTimestamp(timestamp);
  };

  // Helper function to get action description
  const getActionDescription = (log) => {
    const user = getUserName(log.user_id, log.user_email);
    const entityName = log.entity_type.charAt(0).toUpperCase() + log.entity_type.slice(1).replace('_', ' ');
    
    switch (log.action) {
      case 'view':
        if (log.entity_id) {
          return `${user} viewed ${entityName}`;
        }
        return `${user} viewed ${entityName} list`;
      case 'edit':
        return `${user} edited ${entityName}`;
      case 'delete':
        return `${user} deleted ${entityName}`;
      case 'create':
        return `${user} created ${entityName}`;
      case 'assign':
        return `${user} assigned ${entityName}`;
      case 'approve':
        return `${user} approved ${entityName}`;
      case 'reject':
        return `${user} rejected ${entityName}`;
      case 'invite':
        return `${user} invited new team member`;
      default:
        return `${user} performed ${log.action} on ${entityName}`;
    }
  };

  // Helper function to truncate entity ID
  const truncateEntityId = (entityId) => {
    if (!entityId) return '-';
    if (entityId.length <= 12) return entityId;
    return `${entityId.substring(0, 8)}...`;
  };

  // Quick date filter handler
  const handleQuickDateFilter = (filterType) => {
    setQuickDateFilter(filterType);
    const now = new Date();
    let startDate = '';
    let endDate = '';

    switch (filterType) {
      case 'today':
        startDate = now.toISOString().split('T')[0];
        endDate = startDate;
        break;
      case 'yesterday': {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = yesterday.toISOString().split('T')[0];
        endDate = startDate;
        break;
      }
      case 'last7': {
        const last7 = new Date(now);
        last7.setDate(last7.getDate() - 7);
        startDate = last7.toISOString().split('T')[0];
        endDate = now.toISOString().split('T')[0];
        break;
      }
      case 'last30': {
        const last30 = new Date(now);
        last30.setDate(last30.getDate() - 30);
        startDate = last30.toISOString().split('T')[0];
        endDate = now.toISOString().split('T')[0];
        break;
      }
      case 'all':
      default:
        startDate = '';
        endDate = '';
        break;
    }

    setFilterStartDate(startDate);
    setFilterEndDate(endDate);
    setPage(1);
  };

  const fetchActivityLogs = async () => {
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50'
      });
      
      if (filterUserId && filterUserId !== 'all') params.append('user_id', filterUserId);
      if (filterAction && filterAction !== 'all') params.append('action', filterAction);
      if (filterEntityType && filterEntityType !== 'all') params.append('entity_type', filterEntityType);
      if (filterStartDate) params.append('start_date', new Date(filterStartDate).toISOString());
      if (filterEndDate) params.append('end_date', new Date(filterEndDate).toISOString());

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/admin/activity-logs?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setActivityLogs(data.activity_logs || []);
        setTotalLogs(data.total || 0);
        setTotalPages(data.pages || 1);
      } else {
        toast.error('Failed to fetch activity logs');
      }
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      toast.error('Failed to fetch activity logs');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (activityLogs.length === 0) {
      toast.error('No activity logs to export');
      return;
    }

    // Create CSV content
    const headers = ['Timestamp', 'User Email', 'User ID', 'Action', 'Entity Type', 'Entity ID'];
    const rows = activityLogs.map(log => [
      new Date(log.timestamp).toLocaleString(),
      log.user_email,
      log.user_id,
      log.action,
      log.entity_type,
      log.entity_id || '-'
    ]);

    const csvContent = [
      headers.join(','),
      ...(rows || []).map(row => (row || []).map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activity-logs-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast.success('Activity logs exported to CSV');
  };

  const clearFilters = () => {
    setFilterUserId('all');
    setFilterAction('all');
    setFilterEntityType('all');
    setFilterStartDate('');
    setFilterEndDate('');
    setQuickDateFilter('all');
    setPage(1);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getActionBadgeColor = (action) => {
    const colors = {
      view: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      edit: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
      delete: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
      create: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      assign: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
      approve: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
      reject: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
      invite: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
    };
    return colors[action] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold text-navy">Activity Logs</h2>
          <p className="text-sm text-gray-600 mt-1">
            Audit trail of all user actions ({totalLogs} total)
          </p>
        </div>
        <Button 
          onClick={handleExportCSV}
          variant="outline"
          size="sm"
          className="gap-2"
          data-testid="export-csv-button"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-600" />
            <span className="text-sm font-medium text-navy">Filters</span>
          </div>
          {/* Quick Date Filters */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600">Quick filters:</span>
            <div className="flex gap-1">
              <Button data-testid="activity-logs-button-8"
                variant={quickDateFilter === 'today' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleQuickDateFilter('today')}
                className="text-xs h-7"
              >
                Today
              </Button>
              <Button data-testid="activity-logs-button-7"
                variant={quickDateFilter === 'yesterday' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleQuickDateFilter('yesterday')}
                className="text-xs h-7"
              >
                Yesterday
              </Button>
              <Button data-testid="activity-logs-button-6"
                variant={quickDateFilter === 'last7' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleQuickDateFilter('last7')}
                className="text-xs h-7"
              >
                Last 7 days
              </Button>
              <Button data-testid="activity-logs-button-5"
                variant={quickDateFilter === 'last30' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleQuickDateFilter('last30')}
                className="text-xs h-7"
              >
                Last 30 days
              </Button>
              <Button data-testid="activity-logs-button-4"
                variant={quickDateFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleQuickDateFilter('all')}
                className="text-xs h-7"
              >
                All time
              </Button>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {/* User Filter */}
          <div>
            <label className="text-xs text-gray-700 font-medium mb-1 block">User</label>
            <Select data-testid="activity-logs-select-3" value={filterUserId} onValueChange={setFilterUserId}>
              <SelectTrigger data-testid="activity-logs-selecttrigger-3">
                <SelectValue placeholder="All users" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem data-testid="activity-logs-selectitem-18" value="all">All users</SelectItem>
                <SelectItem data-testid="activity-logs-selectitem-17" value="admin">Admin</SelectItem>
                {teamMembers.map(member => (
                  <SelectItem data-testid="activity-logs-selectitem-16" key={member.member_id} value={member.member_id}>
                    {member.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Action Filter */}
          <div>
            <label className="text-xs text-gray-700 font-medium mb-1 block">Action</label>
            <Select data-testid="activity-logs-select-2" value={filterAction} onValueChange={setFilterAction}>
              <SelectTrigger data-testid="activity-logs-selecttrigger-2">
                <SelectValue placeholder="All actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem data-testid="activity-logs-selectitem-15" value="all">All actions</SelectItem>
                <SelectItem data-testid="activity-logs-selectitem-14" value="view">View</SelectItem>
                <SelectItem data-testid="activity-logs-selectitem-13" value="edit">Edit</SelectItem>
                <SelectItem data-testid="activity-logs-selectitem-12" value="create">Create</SelectItem>
                <SelectItem data-testid="activity-logs-selectitem-11" value="delete">Delete</SelectItem>
                <SelectItem data-testid="activity-logs-selectitem-10" value="assign">Assign</SelectItem>
                <SelectItem data-testid="activity-logs-selectitem-9" value="approve">Approve</SelectItem>
                <SelectItem data-testid="activity-logs-selectitem-8" value="reject">Reject</SelectItem>
                <SelectItem data-testid="activity-logs-selectitem-7" value="invite">Invite</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Entity Type Filter */}
          <div>
            <label className="text-xs text-gray-700 font-medium mb-1 block">Entity Type</label>
            <Select data-testid="activity-logs-select-1" value={filterEntityType} onValueChange={setFilterEntityType}>
              <SelectTrigger data-testid="activity-logs-selecttrigger-1">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem data-testid="activity-logs-selectitem-6" value="all">All types</SelectItem>
                <SelectItem data-testid="activity-logs-selectitem-5" value="lead">Leads</SelectItem>
                <SelectItem data-testid="activity-logs-selectitem-4" value="consultation">Consultations</SelectItem>
                <SelectItem data-testid="activity-logs-selectitem-3" value="team_member">Team Members</SelectItem>
                <SelectItem data-testid="activity-logs-selectitem-2" value="comment">Comments</SelectItem>
                <SelectItem data-testid="activity-logs-selectitem-1" value="activity_log">Activity Logs</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Date Range */}
          <div>
            <label className="text-xs text-gray-700 font-medium mb-1 block">Start Date</label>
            <Input data-testid="activity-logs-input-2"
              type="date"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
              className="text-sm"
            />
          </div>

          <div>
            <label className="text-xs text-gray-700 font-medium mb-1 block">End Date</label>
            <Input data-testid="activity-logs-input-1"
              type="date"
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
              className="text-sm"
            />
          </div>
        </div>

        <div className="flex justify-end">
          <Button data-testid="activity-logs-button-3" 
            variant="ghost" 
            size="sm" 
            onClick={clearFilters}
            className="text-xs"
          >
            Clear Filters
          </Button>
        </div>
      </div>

      {/* Activity Logs Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="activity-logs-table">
            <thead className="bg-navy border-b border-gray-300">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                  Time
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                  Action
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                  Description
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                  Entity
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                  Entity ID
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-600">
                    Loading activity logs...
                  </td>
                </tr>
              ) : activityLogs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-600">
                    No activity logs found. Try adjusting your filters.
                  </td>
                </tr>
              ) : (
                activityLogs.map((log, index) => (
                  <motion.tr
                    key={log._id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.02 }}
                    className="hover:bg-gray-50"
                    data-testid={`activity-log-row-${index}`}
                  >
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="font-medium" title={formatTimestamp(log.timestamp)}>
                          {getRelativeTime(log.timestamp)}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(log.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">
                          {getUserName(log.user_id, log.user_email)}
                        </span>
                        <span className="text-xs text-gray-600">
                          {getUserEmail(log.user_id, log.user_email)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionBadgeColor(log.action)}`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {getActionDescription(log)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        <FileText className="h-3 w-3 text-gray-500" />
                        {log.entity_type}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 font-mono">
                      <span title={log.entity_id || 'No entity ID'}>
                        {truncateEntityId(log.entity_id)}
                      </span>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-border flex items-center justify-between">
            <div className="text-sm text-gray-700 font-medium">
              Page {page} of {totalPages}
            </div>
            <div className="flex gap-2">
              <Button data-testid="activity-logs-button-2"
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
              >
                Previous
              </Button>
              <Button data-testid="activity-logs-button-1"
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || loading}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
